import { z } from 'zod';

// Age bounds are capped at 50 to match the vehicle_age_years search param (and stay within the
// backend unsignedSmallInteger column); a rule beyond the searchable age range can never match.
export const optionalInt = z.preprocess(
    (v) => (v === '' || v === null || v === undefined ? undefined : v),
    z.coerce.number().int().min(0).max(50).optional(),
);

// Engine cc can be up to 10000 (matches the ProductController::index engine_size max).
// This is a separate preprocess from optionalInt so age's max:50 cap does not apply.
export const optionalEngineInt = z.preprocess(
    (v) => (v === '' || v === null || v === undefined ? undefined : v),
    z.coerce.number().int().min(0).max(10000).optional(),
);

// Identity dimensions are optional with '' as the "Any" sentinel; a populated value
// must still be a UUID, matching the API contract (nullable|uuid).
export const optionalUuid = (message: string) =>
    z.union([z.string().uuid(message), z.literal('')]).default('');

export const engineRangeOrdered = (data: { engine_min?: number; engine_max?: number }): boolean =>
    data.engine_min === undefined || data.engine_max === undefined || data.engine_min <= data.engine_max;

export const ageRangeOrdered = (data: { age_min?: number; age_max?: number }): boolean =>
    data.age_min === undefined || data.age_max === undefined || data.age_min <= data.age_max;

export const planVehicleRuleSchema = z
    .object({
        make_id: optionalUuid('Select a valid make'),
        model_id: optionalUuid('Select a valid model'),
        derivative_id: optionalUuid('Select a valid derivative'),
        transmission_id: optionalUuid('Select a valid transmission'),
        fuel_type_id: optionalUuid('Select a valid fuel type'),
        engine_min: optionalEngineInt,
        engine_max: optionalEngineInt,
        age_min: optionalInt,
        age_max: optionalInt,
        requires_4x4: z.enum(['', '4x4', 'non_4x4']).default(''),
    })
    .refine(engineRangeOrdered, { message: 'Engine max must be at least engine min', path: ['engine_max'] })
    .refine(ageRangeOrdered, { message: 'Age max must be at least age min', path: ['age_max'] });

export type PlanVehicleRuleFormInput = z.input<typeof planVehicleRuleSchema>;
export type PlanVehicleRuleFormOutput = z.output<typeof planVehicleRuleSchema>;
